import { FC } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DEFAULT_SCORE_LABELS,
  IndicatorValues,
  PatternValues,
  ScoreValues,
} from './EditIndicatorsDialog';

interface IndicatorFormProps {
  index: number;
  values: IndicatorValues;
  onChange: (updated: IndicatorValues) => void;
  onRemove: () => void;
}

const SHARED_FIELDS: Array<{
  key: 'indicator' | 'description' | 'reason' | 'probe' | 'trigger';
  label: string;
}> = [
  { key: 'indicator', label: 'Indicator' },
  { key: 'description', label: 'Description' },
  { key: 'reason', label: 'Reason' },
  { key: 'probe', label: 'Probe' },
  { key: 'trigger', label: 'Trigger' },
];

const SCORE_OPTIONS = [1, 2, 3, 4, 5];

const emptyPattern = (): PatternValues => ({ pattern: '', example: '' });
const emptyScore = (): ScoreValues => ({
  score: 1,
  label: DEFAULT_SCORE_LABELS[0],
  gateway_criteria: [],
  differentiation: '',
});

const IndicatorForm: FC<IndicatorFormProps> = ({
  index,
  values,
  onChange,
  onRemove,
}) => {
  const updateShared = (
    field: 'indicator' | 'description' | 'reason' | 'probe' | 'trigger',
    value: string,
  ) => {
    onChange({ ...values, [field]: value });
  };

  const updateScore = (
    scoreIndex: number,
    patch: Partial<ScoreValues>,
  ) => {
    onChange({
      ...values,
      scores: values.scores.map((s, i) =>
        i === scoreIndex ? { ...s, ...patch } : s,
      ),
    });
  };

  const addScore = () => {
    onChange({ ...values, scores: [...values.scores, emptyScore()] });
  };

  const removeScore = (scoreIndex: number) => {
    onChange({
      ...values,
      scores: values.scores.filter((_, i) => i !== scoreIndex),
    });
  };

  const updateGatewayCriterion = (
    scoreIndex: number,
    criterionIndex: number,
    value: string,
  ) => {
    updateScore(scoreIndex, {
      gateway_criteria: values.scores[scoreIndex].gateway_criteria.map(
        (c, i) => (i === criterionIndex ? value : c),
      ),
    });
  };

  const addGatewayCriterion = (scoreIndex: number) => {
    updateScore(scoreIndex, {
      gateway_criteria: [...values.scores[scoreIndex].gateway_criteria, ''],
    });
  };

  const removeGatewayCriterion = (
    scoreIndex: number,
    criterionIndex: number,
  ) => {
    updateScore(scoreIndex, {
      gateway_criteria: values.scores[scoreIndex].gateway_criteria.filter(
        (_, i) => i !== criterionIndex,
      ),
    });
  };

  const updatePattern = (
    list: 'red_flags' | 'score_reducing_patterns',
    patternIndex: number,
    patch: Partial<PatternValues>,
  ) => {
    onChange({
      ...values,
      negative_evidence: {
        ...values.negative_evidence,
        [list]: values.negative_evidence[list].map((p, i) =>
          i === patternIndex ? { ...p, ...patch } : p,
        ),
      },
    });
  };

  const addPattern = (list: 'red_flags' | 'score_reducing_patterns') => {
    onChange({
      ...values,
      negative_evidence: {
        ...values.negative_evidence,
        [list]: [...values.negative_evidence[list], emptyPattern()],
      },
    });
  };

  const removePattern = (
    list: 'red_flags' | 'score_reducing_patterns',
    patternIndex: number,
  ) => {
    onChange({
      ...values,
      negative_evidence: {
        ...values.negative_evidence,
        [list]: values.negative_evidence[list].filter(
          (_, i) => i !== patternIndex,
        ),
      },
    });
  };

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">Indicator {index + 1}</Typography>
        <IconButton size="small" onClick={onRemove} color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {SHARED_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          label={label}
          value={values[key]}
          onChange={e => updateShared(key, e.target.value)}
          fullWidth
          size="small"
          margin="dense"
          multiline={key !== 'indicator'}
          minRows={key !== 'indicator' ? 2 : 1}
        />
      ))}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Scores
      </Typography>
      {values.scores.map((score, scoreIndex) => (
        <Box
          key={scoreIndex}
          sx={{
            border: '1px dashed #ccc',
            borderRadius: 1,
            p: 1.5,
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Score {scoreIndex + 1}
            </Typography>
            <IconButton
              size="small"
              onClick={() => removeScore(scoreIndex)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Score"
              select
              value={score.score}
              onChange={e =>
                updateScore(scoreIndex, { score: Number(e.target.value) })
              }
              size="small"
              margin="dense"
              sx={{ minWidth: 100 }}
            >
              {SCORE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Label"
              value={score.label}
              onChange={e =>
                updateScore(scoreIndex, { label: e.target.value })
              }
              size="small"
              margin="dense"
              fullWidth
            />
          </Box>
          <TextField
            label="Differentiation"
            value={score.differentiation}
            onChange={e =>
              updateScore(scoreIndex, { differentiation: e.target.value })
            }
            size="small"
            margin="dense"
            fullWidth
            multiline
            minRows={2}
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            Gateway criteria
          </Typography>
          {score.gateway_criteria.map((criterion, ci) => (
            <Box
              key={ci}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TextField
                value={criterion}
                onChange={e =>
                  updateGatewayCriterion(scoreIndex, ci, e.target.value)
                }
                size="small"
                margin="dense"
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => removeGatewayCriterion(scoreIndex, ci)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addGatewayCriterion(scoreIndex)}
          >
            Add criterion
          </Button>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addScore}>
        Add score
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Negative evidence — Red flags
      </Typography>
      {values.negative_evidence.red_flags.map((pattern, pi) => (
        <PatternRow
          key={pi}
          pattern={pattern}
          onChange={patch => updatePattern('red_flags', pi, patch)}
          onRemove={() => removePattern('red_flags', pi)}
        />
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => addPattern('red_flags')}
      >
        Add red flag
      </Button>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Negative evidence — Score-reducing patterns
      </Typography>
      {values.negative_evidence.score_reducing_patterns.map((pattern, pi) => (
        <PatternRow
          key={pi}
          pattern={pattern}
          onChange={patch =>
            updatePattern('score_reducing_patterns', pi, patch)
          }
          onRemove={() => removePattern('score_reducing_patterns', pi)}
        />
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => addPattern('score_reducing_patterns')}
      >
        Add score-reducing pattern
      </Button>
    </Box>
  );
};

interface PatternRowProps {
  pattern: PatternValues;
  onChange: (patch: Partial<PatternValues>) => void;
  onRemove: () => void;
}

const PatternRow: FC<PatternRowProps> = ({ pattern, onChange, onRemove }) => (
  <Box
    sx={{
      border: '1px dashed #ccc',
      borderRadius: 1,
      p: 1.5,
      mb: 1,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <IconButton size="small" onClick={onRemove} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
    <TextField
      label="Pattern"
      value={pattern.pattern}
      onChange={e => onChange({ pattern: e.target.value })}
      size="small"
      margin="dense"
      fullWidth
      multiline
      minRows={2}
    />
    <TextField
      label="Example"
      value={pattern.example}
      onChange={e => onChange({ example: e.target.value })}
      size="small"
      margin="dense"
      fullWidth
      multiline
      minRows={2}
    />
  </Box>
);

export default IndicatorForm;
